import {
  APIPaginatedRequest,
  CountRequest,
  DeleteRequest,
  getFetcherV2,
  GetRequest,
  patchFetcherV2,
  PermissionEntry,
  UpdatePermissionRequest,
} from "shared/api/api";
import { clientV2 } from "shared/api/axios";
import { createURL } from "shared/api/utils";

const DIRECTIVES_BASE_ROUTE = "directives";

export interface Directive {
  ID: string;
  displayName: string;
  slashInvocationName: string;
  directiveText: string | null;
  availableInFrontend: boolean;
  availableAsRecognizedWorkflow: boolean;
  recognizedWorkflowDescription: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  usageCount: number;
  access: PermissionEntry[];
  canEdit: boolean;
}

export interface GetDirectiveRequest extends GetRequest {
  skipRequest?: boolean;
}

export interface ListDirectivesRequest extends APIPaginatedRequest {
  skipRequest?: boolean;
}

export const listDirectivesRequestURI = ({
  skipRequest,
  ...params
}: ListDirectivesRequest): string =>
  skipRequest
    ? ""
    : clientV2.getUri({
        method: "get",
        url: createURL([DIRECTIVES_BASE_ROUTE]),
        params,
      });

export const listDirectives = (params: APIPaginatedRequest) =>
  getFetcherV2<Directive[]>(listDirectivesRequestURI(params));

export const getDirectiveRequestURI = ({
  id,
  skipRequest,
}: GetDirectiveRequest): string =>
  skipRequest || !id
    ? ""
    : clientV2.getUri({
        method: "get",
        url: createURL([DIRECTIVES_BASE_ROUTE, id]),
      });

export const countDirectivesRequestURI = (params: CountRequest): string =>
  clientV2.getUri({
    method: "get",
    url: createURL([DIRECTIVES_BASE_ROUTE, "count"]),
    params,
  });

const deleteDirectiveRequestURI = ({ id }: DeleteRequest): string =>
  clientV2.getUri({
    method: "delete",
    url: createURL([DIRECTIVES_BASE_ROUTE, id]),
  });

export const deleteDirective = (args: DeleteRequest) =>
  clientV2.delete(deleteDirectiveRequestURI(args));

export interface CreateDirectiveRequest {
  displayName: string;
  slashInvocationName: string;
  directiveText?: string | null;
  availableInFrontend?: boolean;
  availableAsRecognizedWorkflow?: boolean;
  recognizedWorkflowDescription?: string | null;
}

const createDirectiveRequestURI = (): string =>
  clientV2.getUri({
    method: "post",
    url: createURL([DIRECTIVES_BASE_ROUTE]),
  });

export const createDirective = (data: CreateDirectiveRequest) =>
  clientV2.post<Directive>(createDirectiveRequestURI(), data);

export interface UpdateDirectiveRequest {
  id: string;
  displayName?: string;
  slashInvocationName?: string;
  directiveText?: string | null;
  availableInFrontend?: boolean;
  availableAsRecognizedWorkflow?: boolean;
  recognizedWorkflowDescription?: string | null;
}

const updateDirectiveRequestURI = ({ id }: UpdateDirectiveRequest): string =>
  clientV2.getUri({
    method: "patch",
    url: createURL([DIRECTIVES_BASE_ROUTE, id]),
  });

export const updateDirective = ({ id, ...data }: UpdateDirectiveRequest) =>
  clientV2.patch<Directive>(updateDirectiveRequestURI({ id }), data);

interface UpdateDirectiveAccessRequest extends Partial<UpdatePermissionRequest> {
  ID: string;
}

const updateDirectiveAccessRequestURI = ({
  ID,
}: UpdateDirectiveAccessRequest): string =>
  clientV2.getUri({
    method: "patch",
    url: createURL([DIRECTIVES_BASE_ROUTE, ID, "access"]),
  });

export const updateDirectiveAccess = ({
  ID,
  ...args
}: UpdateDirectiveAccessRequest) =>
  patchFetcherV2<PermissionEntry[]>(
    updateDirectiveAccessRequestURI({ ID }),
    args
  );
